import {
  PERSIST_IMPERSONATION_KEY,
  PERSIST_ROOT_KEY,
  getTokenUserId,
  isImpersonationTab,
} from "./redux/store-utils";

/**
 * Session sanity checks that must run before the persistor is created.
 *
 * store.ts calls this as its very first statement rather than us relying on
 * import order in index.tsx: with code splitting, whether a given chunk is
 * evaluated before the entry module's body is a bundler decision, and that is
 * far too easy to break silently.
 *
 * Both checks below exist because "who this tab is" was previously derived from
 * two places that can disagree - the persisted redux state and the token in web
 * storage. Whenever those drifted apart, the UI rendered one account while the
 * API returned the other account's data.
 */
export const runSessionBoot = () => {
  const url = new URL(window.location.href);

  /**
   * 1. window.open() clones the opener's sessionStorage into the new tab (per
   *    the HTML spec, session storage is copied when an auxiliary browsing
   *    context is created from an existing one). We cannot opt out of it: the
   *    impersonation handshake needs window.opener, so rel="noopener" is not
   *    available to us.
   *
   *    Without this wipe, switching accounts *from a tab that is already
   *    impersonating* boots the new tab holding the previous account's token,
   *    impersonation flag and persisted state - so it rehydrates as that
   *    account and starts fetching its data before the handshake even lands.
   *
   *    `?imp=1` is only ever present on the initial open, so this runs exactly
   *    once per impersonation tab, before anything of its own is written to it.
   */
  if (url.searchParams.get("imp") === "1") {
    sessionStorage.clear();
  }

  /**
   * 2. The token is the single source of truth for this tab's identity.
   *
   *    Persisted state is easily staler than the token: localStorage is shared
   *    with every other tab, so a login elsewhere swaps the token out from
   *    under this one. If the state we are about to rehydrate belongs to a
   *    different account than the token we will authenticate with, drop the
   *    persisted blob entirely - not just the user slice - so that no other
   *    account's forms, entries or dashboard survive into this session either.
   */
  const impersonating = isImpersonationTab();
  const storage = impersonating ? sessionStorage : localStorage;
  const persistKey = impersonating
    ? PERSIST_IMPERSONATION_KEY
    : PERSIST_ROOT_KEY;

  let persistedUserId: string | undefined;

  try {
    const raw = storage.getItem(persistKey);
    // redux-persist stores each slice as its own JSON string.
    const userSlice = raw ? JSON.parse(raw)?.user : undefined;

    persistedUserId = userSlice
      ? JSON.parse(userSlice)?.loggedInUserId
      : undefined;
  } catch {
    persistedUserId = undefined;
  }

  // An absent token counts as a mismatch: it means this tab is logged out, and
  // any leftover state belongs to a session that no longer exists.
  if (persistedUserId && persistedUserId !== getTokenUserId()) {
    storage.removeItem(persistKey);
  }
};
